import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import {
    formateDateWithMonth,
    handleServerValidations,
    globalLoader
} from "../../../../utils/commonfunction";
import Sorting from "../../common/sorting";
import Loader from "../../common/loader";
import Breadcrums from "../../common/breadcrumbs";
import CustomPagination from "../../common/custompagination";
import { List, UploadReport, UpdateStatus, GetDownloadLogs, RefundReportBooking } from "../../services/reportBooking.services";
import moment from "moment";

const ReportBookingTable = () => {
    const { t } = useTranslation();
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [list, setList] = useState([]);
    const [statsupdate, setStatusUpdate] = useState(false);
    const [datalength, setDataLength] = useState("");
    const [itemperpage] = useState(10);
    const [sorting, setSorting] = useState({});
    const [defaultSorting, setDefaultSorting] = useState(true);
    const [page, setPage] = useState(1);
    const [loader, setLoader] = useState(true);
    const [search, setSearch] = useState({});
    const [globalsearch, setGlobalSearch] = useState("");
    const [reportType, setReportType] = useState("Personalized");
    
    const fileInputRef = useRef(null);
    const [selectedBookingId, setSelectedBookingId] = useState(null);

    const [showLogModal, setShowLogModal] = useState(false);
    const [logsList, setLogsList] = useState([]);
    const [selectedBookingForLogs, setSelectedBookingForLogs] = useState(null);
    const [logsLoader, setLogsLoader] = useState(false);

    const resetFilter = (e) => {
        if (e) e.preventDefault();
        setGlobalSearch("");
        setSearch({});
    };

    const handleRefund = (booking) => {
        const isCancelledOrRefunded = booking.booking_status === "cancelled" || booking.payment?.payment_status === "refunded" || booking.payment?.payment_status === "refund_initiated";
        if (isCancelledOrRefunded) {
            Swal.fire({
                icon: "info",
                text: "This booking is already refunded or cancelled.",
                ...SWAL_SETTINGS,
            });
            return;
        }

        const paymentMethodText = booking.payment?.payment_method ? booking.payment.payment_method.toUpperCase() : "Wallet/Razorpay";

        Swal.fire({
            customClass: "swal-wide",
            title: "Initiate Refund",
            html: `Are you sure you want to refund this booking?<br/><br/>
                   <b>User:</b> ${booking?.user?.first_name || ""} ${booking?.user?.last_name || ""} (${booking?.user?.email || "N/A"})<br/>
                   <b>Amount:</b> ${booking.currency_type || "INR"} ${booking.amount}<br/>
                   <b>Payment Method:</b> ${paymentMethodText}`,
            icon: "warning",
            showCancelButton: true,
            cancelButtonText: t("btn_cancel"),
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Refund",
        }).then((result) => {
            if (result.isConfirmed) {
                globalLoader(true);
                RefundReportBooking({ id: booking.id })
                    .then((res) => {
                        if (res.status || res.success) {
                            Swal.fire({
                                icon: "success",
                                text: res.message || "Refund processed successfully!",
                                ...SWAL_SETTINGS,
                            });
                            setStatusUpdate(!statsupdate);
                        } else {
                            Swal.fire({
                                icon: "error",
                                text: handleServerValidations(res),
                                ...SWAL_SETTINGS,
                            });
                        }
                        globalLoader(false);
                    })
                    .catch((err) => {
                        console.error(err);
                        Swal.fire({
                            icon: "error",
                            text: err?.response?.data?.message || "Refund failed. Please try again.",
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                    });
            }
        });
    };

    const openLogModal = (booking) => {
        setSelectedBookingForLogs(booking);
        setShowLogModal(true);
        setLogsLoader(true);
        GetDownloadLogs({ booking_id: booking.id })
            .then((res) => {
                setLogsList(res?.data || []);
                setLogsLoader(false);
            })
            .catch((err) => {
                console.error(err);
                setLogsLoader(false);
            });
    };

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "Reports", url: "" },
        { title: `${reportType} Reports`, url: "" },
    ];

    useEffect(() => {
        const getData = setTimeout(() => {
            setLoader(true);
            const formData = new FormData();
            formData.append("page", params.pgno || 1);
            formData.append("per_page", itemperpage);
            formData.append("sort", JSON.stringify(sorting));
            formData.append("search", JSON.stringify({ ...search, report_type: reportType }));
            formData.append("global_search", globalsearch);

            List(formData)
                .then((data) => {
                    setDataLength(data?.data?.total_records || 0);
                    setList(data?.data?.list || []);
                    setPage(data?.data?.page || 1);
                    setLoader(false);
                })
                .catch((error) => {
                    console.log("error ====> ", error);
                    setLoader(false);
                });
        }, 300);
        return () => clearTimeout(getData);
    }, [location, statsupdate, sorting, search, globalsearch, itemperpage, params.pgno, reportType]);

    const handleSort = (e, column) => {
        setDefaultSorting(false);
        let sort = { order: 0, column: column };
        if (e.target.classList.contains("assc")) {
            sort.order = -1;
        } else {
            sort.order = 1;
        }
        setSorting(sort);
    };

    const handleUploadClick = (id) => {
        setSelectedBookingId(id);
        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && selectedBookingId) {
            const formData = new FormData();
            formData.append("id", selectedBookingId);
            formData.append("report_file", file);
            
            globalLoader(true);
            UploadReport(formData)
                .then(res => {
                    if (res.status || res.success) {
                        Swal.fire({
                            icon: "success",
                            text: res.message,
                            ...SWAL_SETTINGS,
                        });
                        setStatusUpdate(!statsupdate);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(res),
                            ...SWAL_SETTINGS,
                        });
                    }
                    globalLoader(false);
                    e.target.value = null; // Reset file input
                })
                .catch(err => {
                    console.error(err);
                    globalLoader(false);
                    e.target.value = null;
                });
        }
    };

    const handleStatusChange = (id, newStatus) => {
        Swal.fire({
            customClass: "swal-wide",
            title: t("msg_are_you_sure"),
            text: `Change booking status to "${newStatus}"?`,
            icon: "warning",
            showCancelButton: true,
            cancelButtonText: t("btn_cancel"),
            confirmButtonColor: "#403fad",
            cancelButtonColor: "#f1388b",
            confirmButtonText: t("btn_yes"),
        }).then((result) => {
            if (result.isConfirmed) {
                const formData = new FormData();
                formData.append("id", id);
                formData.append("booking_status", newStatus);

                globalLoader(true);
                UpdateStatus(formData)
                    .then((res) => {
                        if (res.status || res.success) {
                            Swal.fire({
                                icon: "success",
                                text: res.message || "Status updated successfully!",
                                ...SWAL_SETTINGS,
                            });
                            setStatusUpdate(!statsupdate);
                        } else {
                            Swal.fire({
                                icon: "error",
                                text: handleServerValidations(res),
                                ...SWAL_SETTINGS,
                            });
                        }
                        globalLoader(false);
                    })
                    .catch((err) => {
                        console.error(err);
                        Swal.fire({
                            icon: "error",
                            text: "Something went wrong. Please try again.",
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                    });
            }
        });
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="main-content-label">{reportType} Report Bookings</h6>
                            <div className="d-flex align-items-center">
                                <div className="form-group mb-0 me-2">
                                    <div className="form-group mb-0 rangepicker_container filter_design">
                                        <i className="fa fa-search calender_icon"></i>
                                        <input
                                            type="search"
                                            className="form-control"
                                            value={globalsearch}
                                            placeholder={t("input_placeholder_search")}
                                            onChange={(e) => setGlobalSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <button type="reset" onClick={resetFilter} className="btn btn-warning">
                                    {t("btn_reset_filter")}
                                </button>
                            </div>
                        </div>

                        <ul className="nav nav-tabs mb-3">
                            <li className="nav-item">
                                <a
                                    className={`nav-link ${reportType === "Personalized" ? "active" : ""}`}
                                    onClick={() => {
                                        setReportType("Personalized");
                                        setPage(1);
                                    }}
                                    style={{ cursor: "pointer" }}
                                >
                                    Personalized Reports
                                </a>
                            </li>
                            <li className="nav-item">
                                <a
                                    className={`nav-link ${reportType === "Instant" ? "active" : ""}`}
                                    onClick={() => {
                                        setReportType("Instant");
                                        setPage(1);
                                    }}
                                    style={{ cursor: "pointer" }}
                                >
                                    Instant Reports
                                </a>
                            </li>
                        </ul>

                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            style={{ display: "none" }} 
                            onChange={handleFileChange}
                            accept=".pdf,.doc,.docx"
                        />

                        <div className="table-responsive">
                            <table className="table table-bordered border-t0 key-buttons text-nowrap w-100">
                                <thead>
                                    <tr>
                                        <th>{t("list_heading_sno")}</th>
                                        <th>Order Number</th>
                                        <th>User</th>
                                        <th>Report Title</th>
                                        <th>Amount</th>
                                        <th>Booking Date</th>
                                        <th>Status</th>
                                        <th>Report File</th>
                                        <th>Downloaded</th>
                                        <th>Downloads</th>
                                        <th>Last IP</th>
                                        <th className="action_head">{t("list_heading_action")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loader && datalength > 0 ? (
                                        <tr>
                                            <td colSpan={12}>
                                                <Loader />
                                            </td>
                                        </tr>
                                    ) : (
                                        <>
                                            {list.length ? (
                                                list.map((row, i) => (
                                                    <tr key={i}>
                                                        <td>{(page - 1) * itemperpage + i + 1}</td>
                                                        <td className="fw-bold text-primary">{row?.order_number || `ORD-REP-${row?.id}`}</td>
                                                        <td>
                                                            {row?.user?.first_name} {row?.user?.last_name}
                                                            <br/>
                                                            <small className="text-muted">{row?.user?.email}</small>
                                                        </td>
                                                        <td>{row?.report?.title || "N/A"}</td>
                                                        <td>{row?.currency_type} {row?.amount}</td>
                                                        <td>{formateDateWithMonth(row?.booking_date || row?.createdAt)}</td>
                                                         <td>
                                                             {reportType !== "Instant" ? (
                                                                 <div className="d-flex align-items-center gap-2">
                                                                     <span className={`badge ${
                                                                         row.booking_status === 'completed' ? 'bg-success' :
                                                                         row.booking_status === 'in_progress' ? 'bg-primary' :
                                                                         row.booking_status === 'cancelled' ? 'bg-danger' :
                                                                         row.booking_status === 'confirmed' ? 'bg-info' :
                                                                         'bg-warning'
                                                                     }`}>
                                                                         {row.booking_status}
                                                                     </span>
                                                                     <select
                                                                         className="form-select form-select-sm w-auto"
                                                                         value={row.booking_status}
                                                                         onChange={(e) => handleStatusChange(row.id, e.target.value)}
                                                                         style={{ padding: '0.25rem 1.75rem 0.25rem 0.5rem', fontSize: '0.75rem' }}
                                                                     >
                                                                         <option value="pending">Pending</option>
                                                                         <option value="confirmed">Confirmed</option>
                                                                         <option value="in_progress">In Progress</option>
                                                                         <option value="completed">Completed</option>
                                                                     </select>
                                                                 </div>
                                                             ) : (
                                                                 <span className={`badge ${row.booking_status === 'completed' ? 'bg-success' : 'bg-warning'}`}>
                                                                     {row.booking_status}
                                                                 </span>
                                                             )}
                                                         </td>
                                                        <td>
                                                            {row.report_file ? (
                                                                <a href={row.report_file} target="_blank" rel="noreferrer" className="btn btn-sm btn-info">
                                                                    <i className="fa fa-download me-1"></i> View Report
                                                                </a>
                                                            ) : row.url ? (
                                                                <a href={row.url} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-info" title={row.url}>
                                                                    <i className="fa fa-external-link me-1"></i> View URL
                                                                </a>
                                                            ) : (
                                                                <span className="text-muted">Not Uploaded</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {row.is_downloaded || row.download_count > 0 ? (
                                                                <span className="badge bg-success">Downloaded</span>
                                                            ) : (
                                                                <span className="badge bg-secondary">Not Downloaded</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="badge bg-primary">{row.download_count || 0} times</span>
                                                        </td>
                                                        <td>
                                                            <code>{row.last_download_ip || "N/A"}</code>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <button
                                                                    className="btn btn-sm btn-info"
                                                                    onClick={() => navigate("/admin/report/booking-view", { state: { booking: row } })}
                                                                >
                                                                    <i className="fa fa-eye me-1"></i> View
                                                                </button>
                                                                {/* <button
                                                                    className="btn btn-sm btn-warning"
                                                                    onClick={() => openLogModal(row)}
                                                                    title="View IP Download Logs"
                                                                >
                                                                    <i className="fa fa-history me-1"></i> IP Logs
                                                                </button> */}
                                                                <button
                                                                    className="btn btn-sm ripple btn-main-primary signbtn"
                                                                    onClick={() => handleUploadClick(row.id)}
                                                                >
                                                                    <i className="fa fa-upload me-1"></i> 
                                                                    {row.report_file ? "Re-upload" : "Upload Report"}
                                                                </button>
                                                                <button
                                                                    className={`btn btn-sm ${row.booking_status === 'cancelled' || row.payment?.payment_status === 'refunded' || row.payment?.payment_status === 'refund_initiated' ? 'btn-secondary' : 'btn-danger'}`}
                                                                    disabled={row.booking_status === 'cancelled' || row.payment?.payment_status === 'refunded' || row.payment?.payment_status === 'refund_initiated'}
                                                                    onClick={() => handleRefund(row)}
                                                                    title={row.booking_status === 'cancelled' || row.payment?.payment_status === 'refunded' ? "Already Refunded" : "Refund Booking"}
                                                                >
                                                                    <i className="fa fa-undo me-1"></i> {row.booking_status === 'cancelled' || row.payment?.payment_status === 'refunded' || row.payment?.payment_status === 'refund_initiated' ? "Refunded" : "Refund"}
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={12} className="text-center">
                                                        {t("msg_no_records")}
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-3">
                            <b>{t("msg_total_records")} : {datalength ? datalength : "0"}</b>
                        </div>
                        {datalength && datalength > 0 ? (
                            <CustomPagination
                                datalength={datalength}
                                itemperpage={itemperpage}
                                currentPage={page}
                                setPage={setPage}
                                pageRoute={[
                                    {
                                        name: `${reportType} Reports`,
                                        path: "/admin/report/booking-list",
                                    },
                                ]}
                            />
                        ) : (
                            ""
                        )}
                    </div>
                </div>
            </div>

            {showLogModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} tabIndex="-1">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    IP Download History - {selectedBookingForLogs?.report?.title || "Report"} (Booking #{selectedBookingForLogs?.id})
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowLogModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <p className="mb-1"><strong>User Name:</strong> {selectedBookingForLogs?.user?.first_name} {selectedBookingForLogs?.user?.last_name || ""}</p>
                                        <p className="mb-1"><strong>Email:</strong> {selectedBookingForLogs?.user?.email}</p>
                                    </div>
                                    <div className="col-md-6">
                                        <p className="mb-1"><strong>Total Downloads:</strong> <span className="badge bg-info">{selectedBookingForLogs?.download_count || 0} times</span></p>
                                        <p className="mb-1"><strong>Last Download IP:</strong> <code>{selectedBookingForLogs?.last_download_ip || "N/A"}</code></p>
                                    </div>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped text-nowrap">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>IP Address</th>
                                                <th>Downloaded Date</th>
                                                <th>Downloaded Time</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logsLoader ? (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-3">
                                                        <Loader />
                                                    </td>
                                                </tr>
                                            ) : logsList.length > 0 ? (
                                                logsList.map((log, idx) => {
                                                    const logTime = log.downloaded_at || log.createdAt;
                                                    return (
                                                        <tr key={idx}>
                                                            <td>{idx + 1}</td>
                                                            <td><code>{log.ip_address || "N/A"}</code></td>
                                                            <td>{logTime ? moment(logTime).format("DD MMM YYYY") : "N/A"}</td>
                                                            <td>{logTime ? moment(logTime).format("hh:mm:ss A") : "N/A"}</td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center py-3 text-muted">
                                                        No IP download history logs recorded yet.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowLogModal(false)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ReportBookingTable;
